import paymentsService from '@/modules/payments/services/paymentsService';
import PaymentPlansSummaryStore from '@/modules/payments/store/paymentPlansSummaryStore';
import {
  DEFAULT_PER_PAGE,
  MAX_PER_PAGE,
  PAYMENT_PLANS_TABS,
  TAB_DEFAULT_SORT,
  TILE_FILTERS,
  getInitialFilters,
} from '@/modules/payments/constants/paymentPlans';
import { buildPaymentPlansQuery, getActiveFilterKeys, getSelectedTiles } from '@/modules/payments/utils/paymentPlansQueryUtil';
import logError from '@/utils/logError/logError';

let latestRequestId = 0;

const state = {
  tab: PAYMENT_PLANS_TABS.plans,
  query: { page: 1, perPage: DEFAULT_PER_PAGE, sort: { ...TAB_DEFAULT_SORT[PAYMENT_PLANS_TABS.plans] } },
  filters: getInitialFilters(),
  plans: [],
  total: 0,
  isLoaded: false,
  isLoading: false,
  hasError: false,
  isInfiniteScroll: false,
  hasNextPageError: false,
  isRefreshing: false,
};

const getters = {
  tab: (state) => state.tab,
  query: (state) => state.query,
  filters: (state) => state.filters,
  plans: (state) => state.plans,
  total: (state) => state.total,
  isLoading: (state) => state.isLoading,
  hasError: (state) => state.hasError,
  hasMorePlans: (state) => state.plans.length < state.total,
  isInfiniteScroll: (state) => state.isInfiniteScroll,
  hasNextPageError: (state) => state.hasNextPageError,
  isRefreshing: (state) => state.isRefreshing,
  activeFilterKeys: (state) => getActiveFilterKeys(state.filters, state.tab),
  hasActiveFilters: (state, getters) => getters.activeFilterKeys.length > 0,
  hasNoRows: (state) => state.isLoaded && !state.isLoading && !state.hasError && state.total === 0,
  isEmptyBusiness: (state, getters) => getters.hasNoRows && !getters.hasActiveFilters,
  hasNoMatches: (state, getters) => getters.hasNoRows && getters.hasActiveFilters,
  selectedTiles: (state) => getSelectedTiles(state.filters),
};

const mutations = {
  setTab(state, tab) {
    state.tab = tab;
  },
  setQuery(state, changes) {
    state.query = { ...state.query, ...changes };
  },
  setFilter(state, { key, value }) {
    state.filters = { ...state.filters, [key]: value };
  },
  resetFilters(state) {
    state.filters = getInitialFilters();
  },
  setPlans(state, { plans, total }) {
    state.plans = plans;
    state.total = total;
    state.isLoaded = true;
  },
  appendPlans(state, { plans, total }) {
    const loadedUids = new Set(state.plans.map(({ uid }) => uid));
    state.plans = [...state.plans, ...plans.filter(({ uid }) => !loadedUids.has(uid))];
    state.total = total;
  },
  setHasNextPageError(state, hasNextPageError) {
    state.hasNextPageError = hasNextPageError;
  },
  setIsRefreshing(state, isRefreshing) {
    state.isRefreshing = isRefreshing;
  },
  setIsInfiniteScroll(state, isInfiniteScroll) {
    state.isInfiniteScroll = isInfiniteScroll;
  },
  resetPlans(state) {
    state.plans = [];
    state.total = 0;
    state.isLoaded = false;
  },
  setIsLoading(state, isLoading) {
    state.isLoading = isLoading;
  },
  setHasError(state, hasError) {
    state.hasError = hasError;
  },
};

const actions = {
  async fetchPlans({ state, commit, dispatch }, { append = false, rows = 0 } = {}) {
    latestRequestId += 1;
    const requestId = latestRequestId;
    commit('setIsLoading', true);
    commit('setHasError', false);
    if (!append) commit('setHasNextPageError', false);
    const query = rows ? { ...state.query, page: 1, perPage: rows } : state.query;
    try {
      const response = await paymentsService.fetchPaymentPlans(buildPaymentPlansQuery({ ...state, query }));
      const { payment_plans: plans, total_payment_plans: total } = response;
      if (requestId !== latestRequestId) return;
      if (append) {
        commit('appendPlans', { plans, total });
        return;
      }
      if (rows) {
        commit('setPlans', { plans, total });
        commit('setQuery', { page: Math.max(Math.floor(plans.length / state.query.perPage), 1) });
        return;
      }
      const lastPage = Math.max(Math.ceil(total / state.query.perPage), 1);
      if (!plans.length && state.query.page > lastPage) {
        commit('setQuery', { page: lastPage });
        dispatch('fetchPlans');
        return;
      }
      commit('setPlans', { plans, total });
    } catch (error) {
      if (requestId !== latestRequestId) return;
      // No automatic retry: the infinite scroll would ask again at once, and fail again, without end
      if (append) {
        commit('setQuery', { page: state.query.page - 1 });
        commit('setHasNextPageError', true);
      } else {
        commit('setHasError', true);
      }
      logError('Failed to fetch the payment plans list', error);
    } finally {
      if (requestId === latestRequestId) commit('setIsLoading', false);
    }
  },
  loadNextPage({ state, getters, commit, dispatch }) {
    if (state.isLoading || state.hasNextPageError || !getters.hasMorePlans) return undefined;
    commit('setQuery', { page: state.query.page + 1 });
    return dispatch('fetchPlans', { append: true });
  },
  retryNextPage({ commit, dispatch }) {
    commit('setHasNextPageError', false);
    return dispatch('loadNextPage');
  },
  setInfiniteScroll({ commit }, isInfiniteScroll) {
    commit('setIsInfiniteScroll', isInfiniteScroll);
  },
  async refresh({ state, commit, dispatch }) {
    if (!state.isInfiniteScroll) {
      return Promise.all([dispatch('fetchPlans'), dispatch('PaymentPlansSummaryStore/fetchSummary')]);
    }
    const rows = Math.min(Math.max(state.plans.length, state.query.perPage), MAX_PER_PAGE);
    commit('setIsRefreshing', true);
    try {
      return await Promise.all([dispatch('fetchPlans', { rows }), dispatch('PaymentPlansSummaryStore/fetchSummary')]);
    } finally {
      commit('setIsRefreshing', false);
    }
  },
  toggleTile({ getters, commit, dispatch }, tile) {
    const { key, value } = TILE_FILTERS[tile];
    if (getters.selectedTiles.includes(tile)) return dispatch('removeFilter', key);
    getters.selectedTiles.forEach((selectedTile) => {
      const selectedKey = TILE_FILTERS[selectedTile].key;
      commit('setFilter', { key: selectedKey, value: getInitialFilters()[selectedKey] });
    });
    return dispatch('setFilter', { key, value });
  },
  setTab({ commit, dispatch }, tab) {
    commit('setTab', tab);
    commit('resetPlans');
    commit('setQuery', { page: 1, sort: { ...TAB_DEFAULT_SORT[tab] } });
    return dispatch('fetchPlans');
  },
  setSort({ commit, dispatch }, sort) {
    commit('setQuery', { sort, page: 1 });
    return dispatch('fetchPlans');
  },
  setPage({ commit, dispatch }, page) {
    commit('setQuery', { page });
    return dispatch('fetchPlans');
  },
  setPerPage({ commit, dispatch }, perPage) {
    commit('setQuery', { perPage, page: 1 });
    return dispatch('fetchPlans');
  },
  setFilter({ commit, dispatch }, { key, value }) {
    commit('setFilter', { key, value });
    commit('setQuery', { page: 1 });
    return dispatch('fetchPlans');
  },
  removeFilter({ commit, dispatch }, key) {
    commit('setFilter', { key, value: getInitialFilters()[key] });
    commit('setQuery', { page: 1 });
    return dispatch('fetchPlans');
  },
  clearFilters({ commit, dispatch }) {
    commit('resetFilters');
    commit('setQuery', { page: 1 });
    return dispatch('fetchPlans');
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
  modules: { PaymentPlansSummaryStore },
};
